import { CircularProgress, styled } from '@mui/material'
import { VersionReportTemplateEntity } from '../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import moment from 'moment'
import { Button } from '../../../../components/buttons'
import {
  LoadingContainer,
  TableContainer,
  TableHeader,
  TableBody,
  TableRow,
  HeaderCell,
  DataCell,
  DialogHeader,
  DialogContent,
  DialogTitle,
  HeaderButtons,
} from './commons'
import { CloseButton, StyledDivider } from '../commons'
import { TemplateActionMenu } from './TemplateActionMenu'
import { memo, useCallback } from 'react'

interface TemplateMenuProps {
  templates: VersionReportTemplateEntity[]
  loading: boolean
  onTemplateSelect: (template: VersionReportTemplateEntity) => void
  onCreateNewTemplate: () => void
  onTemplateUpdate: (template: VersionReportTemplateEntity) => void
  onClose: () => void
}

export const TemplateMenu = ({
  templates,
  loading,
  onTemplateSelect,
  onCreateNewTemplate,
  onTemplateUpdate,
  onClose,
}: TemplateMenuProps) => {
  return (
    <>
      <DialogHeader>
        <DialogTitle>テンプレート一覧</DialogTitle>
        <HeaderButtons>
          <Button size="s" variant="outlined" onClick={onCreateNewTemplate}>
            新しくテンプレートを作成
          </Button>
          <CloseButton onClick={onClose} />
        </HeaderButtons>
      </DialogHeader>
      <StyledDivider />
      <DialogContent>
        {loading ? (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        ) : (
          <TableContainer>
            <TableHeader>
              <HeaderCell width="38%">名称</HeaderCell>
              <HeaderCell width="30%">更新者</HeaderCell>
              <HeaderCell width="25%">更新日時</HeaderCell>
              <HeaderCell width="8%"></HeaderCell>
            </TableHeader>
            <TableBody>
              {templates.map(template => (
                <TemplateRow
                  key={template.uuid}
                  template={template}
                  onTemplateSelect={onTemplateSelect}
                  onTemplateUpdate={onTemplateUpdate}
                />
              ))}
            </TableBody>
          </TableContainer>
        )}
      </DialogContent>
    </>
  )
}

const TemplateRow = memo(
  ({
    template,
    onTemplateSelect,
    onTemplateUpdate,
  }: {
    template: VersionReportTemplateEntity
    onTemplateSelect: (template: VersionReportTemplateEntity) => void
    onTemplateUpdate: (template: VersionReportTemplateEntity) => void
  }) => {
    const handleSelect = useCallback(() => {
      onTemplateSelect(template)
    }, [template, onTemplateSelect])

    const handleUpdate = useCallback(() => {
      onTemplateUpdate(template)
    }, [template, onTemplateUpdate])

    return (
      <TemplateTableRow>
        <DataCell width="38%" onClick={handleSelect}>
          {template.name}
        </DataCell>
        <DataCell width="30%" onClick={handleSelect}>
          {template.updatedBy}
        </DataCell>
        <DataCell width="25%" onClick={handleSelect}>
          {moment(template.updatedAt).format('MM/DD HH:mm')}
        </DataCell>
        <ActionCell width="7%">
          <MenuButtonWrapper className="template-action-menu">
            <TemplateActionMenu onUpdate={handleUpdate} />
          </MenuButtonWrapper>
        </ActionCell>
      </TemplateTableRow>
    )
  }
)

TemplateRow.displayName = 'TemplateRow'

const TemplateTableRow = styled(TableRow)({
  '&:hover .template-action-menu': {
    opacity: 1,
  },
})

const ActionCell = styled('div')<{ width: string }>(({ width }) => ({
  width,
  padding: '12px 8px',
  fontSize: '13px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
}))

const MenuButtonWrapper = styled('div')({
  opacity: 0,
  transition: 'opacity 0.2s',
})
